/**
 * Copyright 2026 Ziz Academy
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { User, LoginCredentials, AuthContextType } from '../types';
import { userApi } from '../services/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(() => {
        const savedUser = localStorage.getItem('user');
        return savedUser ? JSON.parse(savedUser) : null;
    });
    const [isLoading, setIsLoading] = useState(false);

    const login = useCallback(async (credentials: LoginCredentials) => {
        setIsLoading(true);
        try {
            const loggedInUser = await userApi.login(credentials);
            setUser(loggedInUser);
            localStorage.setItem('user', JSON.stringify(loggedInUser));
        } finally {
            setIsLoading(false);
        }
    }, []);

    const register = useCallback(async (userData: User) => {
        setIsLoading(true);
        try {
            const registeredUser = await userApi.register(userData);
            setUser(registeredUser);
            localStorage.setItem('user', JSON.stringify(registeredUser));
        } finally {
            setIsLoading(false);
        }
    }, []);

    const logout = useCallback(() => {
        setUser(null);
        localStorage.removeItem('user');
    }, []);

    const value: AuthContextType = {
        user,
        login,
        register,
        logout,
        isLoading,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};